import { useState, useCallback } from "react";
import dayjs from "dayjs";

import useForm from "../../hooks/useForm";
import { useAverageBasedCalculatorData } from "./useAverageBasedCalculatorData";
import {
  calculateTotalAmountInvested,
  calculateCurrentValue,
  calculateChange,
  calculateChangePercentage,
} from "../utils/calculateAverageBased";
import { getAmountInvested } from "../../services";

/**
 * Hook for managing average based calculator form state and calculations
 * @returns {{
 *   formState: object,
 *   results: object | null,
 *   handleSubmit: () => void,
 *   loading: boolean
 * }}
 */
export const useAverageBasedCalculator = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const formState = useForm(
    {
      numberOfShares: "",
      averageAmountInvested: "",
      currency: "",
    },
    {
      numberOfShares: "required",
      averageAmountInvested: "required",
    }
  );

  const { lastPrice, instrumentId, currencyCode } = useAverageBasedCalculatorData();

  const handleSubmit = useCallback(() => {
    formState.handleSubmit(
      async (data) => {
        setLoading(true);
        try {

          // Get the last price - use form currency if provided, otherwise use default
          const selectedCurrency = data.currency || currencyCode || "";

          const amountInvestedData = await getAmountInvested({
            instrumentId,
            amountInvested: 1,
            startDate: dayjs().subtract(1, 'month').startOf('day').toISOString(),
            endDate: dayjs().endOf('day').toISOString(),
            currency: data.currency,
            dividendChecked: false,
          });

          const price = amountInvestedData?.data?.amountInvested?.data?.endPrice;
          const endDate = amountInvestedData?.data?.amountInvested?.data?.endDate || null;

          // Perform calculations
          const totalAmountInvested = calculateTotalAmountInvested(
            data.numberOfShares,
            data.averageAmountInvested
          );
          const currentValue = calculateCurrentValue(data.numberOfShares, price);
          const change = calculateChange(currentValue, totalAmountInvested);
          const changePercentage = calculateChangePercentage(change, totalAmountInvested);

          setResults({
            lastPrice: price,
            totalAmountInvested,
            currentValue,
            change,
            changePercentage,
            currency: selectedCurrency,
            endDate,
          });
        } catch (error) {
          console.error("Error calculating average based results:", error);
          setResults(null);
        } finally {
          setLoading(false);
        }
      },
      (errors) => {
        console.log("Form validation errors:", errors);
      }
    );
  }, [formState, lastPrice, currencyCode]);

  return {
    formState,
    results,
    handleSubmit,
    loading,
    lastPrice,
    instrumentId,
  };
};

